/**
 * Biased random integer in [min, max], centred on a target derived from
 * `biasPercent` (1 -> min, 100 -> max).
 *
 * Draws a single uniform value from a window centred on the target and
 * rounds it. The window spans the whole range mid-range and narrows towards
 * the edges, which keeps the mean on the target: for a two-value range
 * (e.g. 0..1) `max` is returned with probability `biasPercent / 100`.
 *
 * @param min
 * @param max
 * @param biasPercent - range 1..100. passing 1 (or less) always returns min, 100 (or more) always returns max
 */
function biasedRandomInt(min: number, max: number, biasPercent: number = 50) {
    if (biasPercent <= 1) {
        return min;
    }
    if (biasPercent >= 100) {
        return max;
    }

    const range = max - min;
    const target = min + range * (biasPercent / 100);

    // symmetric noise around the target; full spread mid-range, narrowing
    // towards the edges so the mean stays on the target
    const halfWidth = Math.min(range / 2, target - min + 0.5, max - target + 0.5);
    const noise = (Math.random() * 2 - 1) * halfWidth;

    return Math.min(Math.max(Math.round(target + noise), min), max);
}

/**
 * Given a range, a 'bin' count and a value, return
 * the index of the value into a bin. Useful for converting UI
 * slider ranges into discrete indexes.
 * @param min
 * @param max
 * @param count
 * @param value
 */
function rangeToIndex(min: number, max: number, count: number, value: number): number {
    if (value < min || value > max) {
        throw new Error('value must be within range');
    }

    // 1. Edge case: max maps to the last valid index (count - 1)
    if (value === max) {
        return count - 1;
    }

    const normalizedValue = value - min;
    const rangeSpan = max - min;

    return Math.floor((normalizedValue / rangeSpan) * count);
}

export {biasedRandomInt, rangeToIndex}
